// @ts-nocheck
/**
 * Canonical KPI Category Configuration
 * Single source of truth for all category naming across the application.
 */

export interface KPICategoryDef {
  id: string;
  name: string; // Short uppercase badge text (e.g. "HEALTH")
  label: string; // Full display title (e.g. "Account Health")
  color: string;
  icon: string;
}

export const KPI_CATEGORIES: Record<string, KPICategoryDef> = {
  health: {
    id: "health",
    name: "HEALTH",
    label: "Account Health",
    color: "hsl(142 71% 45%)",
    icon: "💚",
  },
  financial: {
    id: "financial",
    name: "FINANCIAL",
    label: "Financial Performance",
    color: "hsl(45 93% 47%)",
    icon: "💰",
  },
  experience: {
    id: "experience",
    name: "EXPERIENCE",
    label: "Customer Experience",
    color: "hsl(280 75% 65%)",
    icon: "⭐",
  },
  engagement: {
    id: "engagement",
    name: "ENGAGEMENT",
    label: "Product Engagement",
    color: "hsl(210 75% 60%)",
    icon: "📊",
  },
  adoption: {
    id: "adoption",
    name: "ADOPTION",
    label: "Adoption",
    color: "hsl(330 70% 60%)",
    icon: "🚀",
  },
  support: {
    id: "support",
    name: "SUPPORT",
    label: "Support & Risk",
    color: "hsl(0 70% 55%)",
    icon: "🛡️",
  },
  relationships: {
    id: "relationships",
    name: "RELATIONSHIPS",
    label: "Relationships",
    color: "hsl(190 70% 50%)",
    icon: "🤝",
  },
  overall: {
    id: "overall",
    name: "OVERALL",
    label: "Overall",
    color: "hsl(250 60% 55%)",
    icon: "📋",
  },
};

/** Ordered list of category IDs for display */
export const KPI_CATEGORY_ORDER: string[] = [
  "health",
  "financial",
  "experience",
  "engagement",
  "adoption",
  "support",
  "relationships",
];

/** All canonical labels in display order */
export const CATEGORY_LABELS: string[] = KPI_CATEGORY_ORDER.map(
  id => KPI_CATEGORIES[id].label
);

/** Lookup by canonical label (e.g. "Account Health" → category def) */
export function getCategoryByLabel(label: string): KPICategoryDef | undefined {
  return Object.values(KPI_CATEGORIES).find(c => c.label === label);
}

/** Lookup by short ID (e.g. "health" → category def) */
export function getCategoryById(id: string): KPICategoryDef | undefined {
  return KPI_CATEGORIES[id];
}

/** Get display label from category_id (e.g. "health" → "Account Health") */
export function getCategoryLabel(id: string): string {
  return KPI_CATEGORIES[id]?.label || id;
}

/**
 * Aliases to normalize LLM output drift back to canonical labels.
 * Keys are lowercase for case-insensitive matching.
 */
export const SYNOPSIS_KEY_ALIASES: Record<string, string> = {
  "customer health": "Account Health",
  health: "Account Health",
  "platform health": "Account Health",
  "account health": "Account Health",
  adoption: "Adoption & License Utilization",
  "feature adoption": "Adoption & License Utilization",
  "adoption & license utilization": "Adoption & License Utilization",
  "support & risk": "Support & Risk Signals",
  support: "Support & Risk Signals",
  "support & risk signals": "Support & Risk Signals",
  engagement: "Product Engagement",
  "product engagement": "Product Engagement",
  "user engagement": "Product Engagement",
  financial: "Financial Performance",
  "financial performance": "Financial Performance",
  "financial health": "Financial Performance",
  experience: "Customer Experience",
  "customer experience": "Customer Experience",
  "user experience": "Customer Experience",
  relationships: "Relationships Alignment",
  "relationships alignment": "Relationships Alignment",
  "relationship intelligence": "Relationships Alignment",
  overall: "Overall",
  "overall synopsis": "Overall",
  "executive summary": "Overall",
};

/** Normalize a synopsis key (from LLM output) to a canonical label */
export function normalizeSynopsisKey(key: string): string {
  // Direct match against canonical labels first
  const directMatch = getCategoryByLabel(key);
  if (directMatch) return directMatch.label;
  // Alias lookup (case-insensitive)
  return SYNOPSIS_KEY_ALIASES[key.toLowerCase()] || key;
}
